import { useState, useEffect, useMemo, useRef } from "react";
import { TextField, Button, /* Alert, */ Box, IconButton, Link, Typography, Fade, CircularProgress, Select, MenuItem, FormControl, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useFirebase, useAccountCheck } from "../../../../hooks";
import { toast } from "react-toastify";
import { useRegisterWithFirebaseMutation } from "../../../../store/api/usersApiSlice";
import { setCredentials } from "../../../../store/slices/authSlice";
import { /* AgreementsList, */ RegisterPhoneVerification } from "../../../../components";
import { Visibility, VisibilityOff, Person, Email, Lock, School, Phone, Info } from "@mui/icons-material";

import { useGetProfessionsQuery } from "../../../../store/api/professionApiSlice";
import { getCurrentTimezone } from "../../../../utils/timezone";
import InputMask from 'react-input-mask';
import { KvkkAydinlatmaMetniContent } from "../../../../constants/agreements";
import { reportClientAuthError } from "../../../../utils/clientAuthErrorReport";
import { withDoctorRegisterDefaults } from "../../../../utils/doctorAuthHelpers";
import { appColors } from "../../../../constants/theme";
import { getTaxonomyEntityName } from "../../../../utils/taxonomyFields";

const UnifiedRegister = ({ userType, showVerify, setShowVerify, loading, setLoading }) => {
    const { t } = useTranslation();
    
    useEffect(() => {
        // Catch unhandled promise rejections
        const handleUnhandledRejection = (event) => {
            const reason = event.reason;
            const message = reason?.message || reason?.toString() || '';
            const reasonString = reason?.toString() || String(reason) || '';
            const isTimeout = message.toLowerCase().includes('timeout') ||
                reasonString.toLowerCase().includes('timeout') ||
                reason === 'Timeout' ||
                message === 'Timeout';

            if (isTimeout) {
                // Prevent default error handling and show user-friendly message
                event.preventDefault();
                toast.error(t("recaptcha_verification_timeout"));
            }
        };

        window.addEventListener('unhandledrejection', handleUnhandledRejection);

        return () => {
            window.removeEventListener('unhandledrejection', handleUnhandledRejection);
        };
    }, [t]);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [verifyCode, setVerifyCode] = useState("");
    const isSendingCodeRef = useRef(false);
    const { resolveRegisterFirebaseSession, getSignature, deleteUserFirebase, getFCMToken, sendEmailVerificationFirebase, generateRecaptcha, signInWithPhoneNumberFirebase } = useFirebase();
    const [registerWithFirebase] = useRegisterWithFirebaseMutation();
    const { checkAccountAvailability } = useAccountCheck();
    const [passwordVisibility, setPasswordVisibility] = useState(false);
    const [passwordConfirmVisibility, setPasswordConfirmVisibility] = useState(false);
    const isDoctorRegister = userType === "doctor";

    const [showKvkkDialog, setShowKvkkDialog] = useState(false);
    const { data, isLoading: categoriesLoading, error: categoriesError } = useGetProfessionsQuery();
    // Memoize specialtyCategories to prevent infinite loop in useEffect
    const specialtyCategories = useMemo(() => data?.data ?? [], [data?.data]);

    const [lastCodeSentTime, setLastCodeSentTime] = useState(null);
    const [cooldownSeconds, setCooldownSeconds] = useState(0);
    const COOLDOWN_DURATION = 60;
    const COOLDOWN_DURATION_AFTER_ERROR = 900;

    const resetRegisterBusyState = () => {
        isSendingCodeRef.current = false;
        setLoading(false);
    };

    const handleCloseVerify = () => {
        resetRegisterBusyState();
        try {
            if (window.confirmationResult) {
                delete window.confirmationResult;
            }
        } catch (_) { /* noop */ }
        setShowVerify(false);
    };

    useEffect(() => {
        return () => {
            resetRegisterBusyState();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    
    const getStorageKey = (phoneNumber) => {
        const cleanPhone = phoneNumber?.replace(/\s/g, '') || '';
        return `sms_cooldown_${cleanPhone}`;
    };

    const validationSchema = Yup.object().shape({
        firstName: Yup.string()
            .required(t("error_generic_invalid_input"))
            .min(2, t("error_generic_invalid_input"))
            .max(50, t("error_generic_invalid_input")),
        lastName: Yup.string()
            .required(t("error_generic_invalid_input"))
            .min(2, t("error_generic_invalid_input"))
            .max(50, t("error_generic_invalid_input")),
        email: Yup.string()
            .email(t("error_generic_invalid_input"))
            .max(320, t("error_generic_invalid_input"))
            .required(t("error_generic_invalid_input")),
        phoneNumber: Yup.string()
            .required(t("error_generic_invalid_input"))
            .min(13, t("error_generic_invalid_input"))
            .max(13, t("error_generic_invalid_input"))
            .matches(/^[0-9\s]+$/, t("error_generic_invalid_input"))
            .test('phone-format', t("error_generic_invalid_input"), (value) => {
                if (!value) return false;
                const numbersOnly = value.replace(/\s/g, '');
                return numbersOnly.length === 10 && /^[0-9]+$/.test(numbersOnly);
            }),
        password: isDoctorRegister
            ? Yup.string().nullable()
            : Yup.string()
            .required(t("error_generic_password_rules"))
            .min(8, t("error_generic_password_rules"))
            .max(32, t("error_generic_password_rules"))
            .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/, t("error_generic_password_rules")),
        confirmPassword: isDoctorRegister
            ? Yup.string().nullable()
            : Yup.string()
            .required(t("error_generic_password_mismatch"))
            .max(32, t("error_generic_password_mismatch"))
            .oneOf([Yup.ref("password"), null], t("error_generic_password_mismatch")),
        professionId: isDoctorRegister
            ? Yup.string().required(t("error_generic_invalid_input"))
            : Yup.string().nullable(),
    });

    //------------- FORMIK ---------------
    const { handleSubmit, handleChange, handleBlur, values, errors, touched, isValid, dirty, setFieldError, setFieldTouched } = useFormik({
        initialValues: {
            firstName: "",
            lastName: "",
            email: "",
            phoneNumber: "",
            countryCode: "90",
            password: "",
            confirmPassword: "",
            professionId: "",
        },
        validationSchema,
        onSubmit: (values) => {
            handleOnSubmit(values);
        },
    });

    useEffect(() => {
        const cleanPhoneNumber = values.phoneNumber?.replace(/\s/g, '');
        if (cleanPhoneNumber && cleanPhoneNumber.length === 10) {
            const storageKey = getStorageKey(cleanPhoneNumber);
            const stored = localStorage.getItem(storageKey);
            if (stored) {
                try {
                    const { timestamp, duration } = JSON.parse(stored);
                    const now = Date.now();
                    const elapsed = Math.floor((now - timestamp) / 1000);
                    const remaining = duration - elapsed;

                    if (remaining > 0) {
                        setLastCodeSentTime(timestamp);
                        setCooldownSeconds(remaining);
                    } else {
                        localStorage.removeItem(storageKey);
                    }
                } catch (e) {
                    console.error('Cooldown bilgisi okunamadı:', e);
                }
            }
        }
    }, [values.phoneNumber]);

    useEffect(() => {
        const cleanPhoneNumber = values.phoneNumber?.replace(/\s/g, '');
        if (cooldownSeconds > 0 && lastCodeSentTime && cleanPhoneNumber && cleanPhoneNumber.length === 10) {
            const storageKey = getStorageKey(cleanPhoneNumber);
            localStorage.setItem(storageKey, JSON.stringify({
                timestamp: lastCodeSentTime,
                duration: cooldownSeconds + Math.floor((Date.now() - lastCodeSentTime) / 1000)
            }));
            
            const interval = setInterval(() => {
                const now = Date.now();
                const elapsed = Math.floor((now - lastCodeSentTime) / 1000);
                const remaining = cooldownSeconds - elapsed;
                
                if (remaining <= 0) {
                    setCooldownSeconds(0);
                    setLastCodeSentTime(null);
                    localStorage.removeItem(storageKey);
                    clearInterval(interval);
                } else {
                    setCooldownSeconds(remaining);
                    localStorage.setItem(storageKey, JSON.stringify({
                        timestamp: lastCodeSentTime,
                        duration: remaining + elapsed
                    }));
                }
            }, 1000);

            return () => clearInterval(interval);
        }
    }, [cooldownSeconds, lastCodeSentTime, values.phoneNumber]);

    //------------- HANDLERS ---------------
    const handleOnSubmit = async (values) => {
        try {
            setLoading(true);
            const userData = values;

            const timeoutPromise = new Promise((_, reject) => {
                setTimeout(() => {
                    const timeoutError = new Error('Kayıt işlemi zaman aşımına uğradı. Lütfen tekrar deneyin.');
                    timeoutError.name = 'TimeoutError';
                    reject(timeoutError);
                }, 120000);
            });

            const registrationPromise = (async () => {
                const cleanPhoneNumberForSubmit = (userData.phoneNumber || "").replace(/\s/g, '');
                const fullPhoneNumberForSubmit = cleanPhoneNumberForSubmit ? `+90${cleanPhoneNumberForSubmit}` : '';
                if (userData.email && fullPhoneNumberForSubmit) {
                    const formikHelpersForSubmit = {
                        setFieldTouched,
                        setFieldError
                    };

                    const submitCheckResult = await checkAccountAvailability({
                        email: userData.email,
                        phone: fullPhoneNumberForSubmit,
                        formikHelpers: formikHelpersForSubmit,
                        setLoading
                    });

                    if (!submitCheckResult.isValid) {
                        return;
                    }
                }

                const linkResult = await resolveRegisterFirebaseSession({
                    email: userData.email,
                    password: isDoctorRegister ? undefined : userData.password,
                });



                if (linkResult.isSuccess) {
                    const tokenForBackend = linkResult.idToken || (await linkResult.user.getIdToken());
                    const signature = await getSignature(tokenForBackend);
                    const deviceToken = await getFCMToken();

                    const cleanPhoneNumber = userData.phoneNumber.replace(/\s/g, '');

                    const requestData = withDoctorRegisterDefaults({
                        email: userData.email,
                        firstName: userData.firstName,
                        lastName: userData.lastName,
                        userType,
                        phoneNumber: cleanPhoneNumber,
                        countryCode: "90",
                        firebaseToken: tokenForBackend,
                        firebaseUid: linkResult.user.uid,
                        signature,
                        ...(isDoctorRegister ? {} : { password: userData.password }),
                        deviceToken: deviceToken,
                        accountVerifyCode: verifyCode,
                        professionId: isDoctorRegister ? (values.professionId || null) : null,
                        timeZone: getCurrentTimezone(),
                    });

                    const res = await registerWithFirebase(requestData).unwrap();

                    if (res?.isSuccess) {
                        try {
                            sendEmailVerificationFirebase(linkResult.user);
                            toast.success(t("email_confirmation_sent"));
                        } catch (emailError) {
                            console.log('[UnifiedRegister] Email onay e-postası gönderilemedi:', emailError);
                        }

                        dispatch(
                            setCredentials({
                                token: res.data.token,
                                userType: res.data.userType,
                                isEmailVerified: res.data.isEmailVerified,
                                status: res.data.status,
                            })
                        );
                    } else {
                        // TODO: hata mesajı geri açılacaktır.
                        // toast.error(msgs[0]);
                        toast.error(t("error_generic_register_conflict_with_signin_hint"));
                        await deleteUserFirebase();
                    }
                } else {
                    const isFirebaseError = linkResult?.errorCode?.startsWith('auth/') ||
                        linkResult?.errorMessage?.includes('Firebase') ||
                        linkResult?.errorMessage?.includes('too-many-requests') ||
                        linkResult?.errorMessage?.includes('quota-exceeded');

                    if (isFirebaseError) {
                        console.error('[UnifiedRegister] Firebase linkleme hatası (kullanıcıya gösterilmedi):', {
                            errorCode: linkResult?.errorCode,
                            errorMessage: linkResult?.errorMessage,
                            linkResult
                        });
                    } else {
                        // TODO: hata mesajı geri açılacaktır.
                        // toast.error(msg);
                        toast.error(t("error_generic_action_failed"));
                    }
                }
            })();

            await Promise.race([registrationPromise, timeoutPromise]);

        } catch (err) {
            console.error("Kayıt işlemi hatası:", err);

            const isFirebaseError = err?.code?.startsWith('auth/') ||
                err?.message?.includes('Firebase') ||
                err?.message?.includes('too-many-requests') ||
                err?.message?.includes('quota-exceeded') ||
                err?.message?.includes('network-request-failed');

            if (err.name === 'TimeoutError' || err.message?.includes('zaman aşımına uğradı')) {
                toast.error(t("timeout_error"));
            } else if (isFirebaseError) {
                console.error('[UnifiedRegister] Firebase hatası (kullanıcıya gösterilmedi):', {
                    code: err?.code,
                    message: err?.message,
                    error: err
                });
            } else {
                toast.error(t("error_generic_action_failed"));
            }

            try {
                await deleteUserFirebase();
            } catch (deleteError) {
                console.error("Firebase kullanıcı silme hatası:", deleteError);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleSendCode = async () => {

        // Prevent multiple simultaneous calls
        if (isSendingCodeRef.current) {
            return;
        }

        isSendingCodeRef.current = true;
        try {
            setLoading(true);

            const cleanPhoneNumber = values.phoneNumber.replace(/\s/g, '');
            const storageKey = getStorageKey(cleanPhoneNumber);
            const stored = localStorage.getItem(storageKey);

            let remaining = 0;
            if (stored) {
                try {
                    const { timestamp, duration } = JSON.parse(stored);
                    const now = Date.now();
                    const elapsed = Math.floor((now - timestamp) / 1000);
                    remaining = duration - elapsed;

                    if (remaining > 0) {
                        setLastCodeSentTime(timestamp);
                        setCooldownSeconds(remaining);
                    } else {
                        localStorage.removeItem(storageKey);
                    }
                } catch (e) {
                    console.error('Cooldown bilgisi okunamadı:', e);
                }
            }

            // State'ten de kontrol et
            if (remaining <= 0 && lastCodeSentTime && cooldownSeconds > 0) {
                const now = Date.now();
                const elapsed = Math.floor((now - lastCodeSentTime) / 1000);
                remaining = cooldownSeconds - elapsed;
            }

            if (remaining > 0) {
                const minutes = Math.floor(remaining / 60);
                const seconds = remaining % 60;
                const timeText = minutes > 0 ? `${minutes} dakika ${seconds} saniye` : `${seconds} saniye`;
                toast.error(t("please_wait_before_resending_code", { seconds: remaining }) || `Lütfen ${timeText} bekleyin`);
                setLoading(false);
                return;
            }

            if (!values.phoneNumber || cleanPhoneNumber.length !== 10) {
                toast.error(t("please_enter_valid_phone_number"));
                setLoading(false);
                return;
            }

            const fullPhoneNumber = `+90${cleanPhoneNumber}`;

            const formikHelpers = {
                setFieldTouched,
                setFieldError
            };

            const checkResult = await checkAccountAvailability({
                email: values.email,
                phone: fullPhoneNumber,
                formikHelpers,
                setLoading
            });

            if (!checkResult.isValid) {
                return;
            }

            const recaptchaContainer = document.createElement('div');
            recaptchaContainer.id = 'recaptcha-container';
            recaptchaContainer.style.position = 'absolute';
            recaptchaContainer.style.left = '-9999px';
            recaptchaContainer.style.top = '-9999px';
            recaptchaContainer.style.visibility = 'hidden';
            document.body.appendChild(recaptchaContainer);

            // reCAPTCHA'yı oluştur
            let appVerifier;
            try {
                appVerifier = await generateRecaptcha(recaptchaContainer);
            } catch (recaptchaError) {
                setLoading(false);
                setTimeout(() => {
                    try {
                        if (recaptchaContainer) {
                            // Container'ı gizle ama silme - reCAPTCHA widget'ı hala erişmeye çalışabilir
                            recaptchaContainer.style.display = 'none';
                            recaptchaContainer.style.visibility = 'hidden';
                            recaptchaContainer.style.position = 'absolute';
                            recaptchaContainer.style.left = '-9999px';
                            recaptchaContainer.style.top = '-9999px';
                            recaptchaContainer.innerHTML = '';
                        }
                    } catch (e) {
                        console.warn('Container gizleme hatası:', e);
                    }
                }, 1000);
                throw recaptchaError;
            }

            if (!appVerifier) {
                toast.error(t("recaptcha_load_failed"));
                setTimeout(() => {
                    try {
                        if (recaptchaContainer) {
                            // Container'ı gizle ama silme
                            recaptchaContainer.style.display = 'none';
                            recaptchaContainer.style.visibility = 'hidden';
                            recaptchaContainer.style.position = 'absolute';
                            recaptchaContainer.style.left = '-9999px';
                            recaptchaContainer.style.top = '-9999px';
                            recaptchaContainer.innerHTML = '';
                        }
                    } catch (e) {
                        console.warn('Container gizleme hatası:', e);
                    }
                }, 1000);
                setLoading(false);
                return;
            }

            // Firebase telefon doğrulaması
            let confirmationResult;
            try {
                confirmationResult = await signInWithPhoneNumberFirebase(
                    fullPhoneNumber,
                    appVerifier,
                    () => { } // setDialog callback'i
                );
            } catch (signInError) {
                // Timeout veya diğer hataları yakala ve outer catch'e fırlat
                setLoading(false);
                // Cleanup
                try {
                    if (appVerifier && appVerifier.clear) {
                        appVerifier.clear();
                    }
                } catch (e) {
                    console.warn('reCAPTCHA temizleme hatası:', e);
                }
                setTimeout(() => {
                    try {
                        if (recaptchaContainer) {
                            // Container'ı gizle ama silme
                            recaptchaContainer.style.display = 'none';
                            recaptchaContainer.style.visibility = 'hidden';
                            recaptchaContainer.style.position = 'absolute';
                            recaptchaContainer.style.left = '-9999px';
                            recaptchaContainer.style.top = '-9999px';
                            recaptchaContainer.innerHTML = '';
                        }
                    } catch (e) {
                        console.warn('Container gizleme hatası:', e);
                    }
                }, 1000);
                // Re-throw to be caught by outer catch block
                throw signInError;
            }

            if (confirmationResult) {
                window.confirmationResult = confirmationResult;

                setShowVerify(true);

                const now = Date.now();
                setLastCodeSentTime(now);
                setCooldownSeconds(COOLDOWN_DURATION);
                const storageKey = getStorageKey(cleanPhoneNumber);
                localStorage.setItem(storageKey, JSON.stringify({
                    timestamp: now,
                    duration: COOLDOWN_DURATION
                }));

                toast.success(t("verification_code_sent"));

                // reCAPTCHA widget'ı temizle - container'ı silme, sadece gizle
                try {
                    if (appVerifier && appVerifier.clear) {
                        appVerifier.clear();
                    }
                } catch (e) {
                    console.warn('reCAPTCHA temizleme hatası:', e);
                }
                if (window.recaptchaVerifier === appVerifier) {
                    try { delete window.recaptchaVerifier; } catch (_) { }
                }
                if (window.recaptchaContainer === recaptchaContainer) {
                    try { delete window.recaptchaContainer; } catch (_) { }
                }
                // Container'ı silmek yerine sadece gizle - reCAPTCHA widget'ı hala erişmeye çalışabilir
                // Container'ı silmek "Cannot read properties of null" hatasına neden olur
                setTimeout(() => {
                    try {
                        if (recaptchaContainer) {
                            // Container'ı gizle ama silme
                            recaptchaContainer.style.display = 'none';
                            recaptchaContainer.style.visibility = 'hidden';
                            recaptchaContainer.style.position = 'absolute';
                            recaptchaContainer.style.left = '-9999px';
                            recaptchaContainer.style.top = '-9999px';
                            // İçeriği temizle
                            recaptchaContainer.innerHTML = '';
                        }
                    } catch (e) {
                        console.warn('Container gizleme hatası:', e);
                    }
                }, 1000); // 1 saniye bekle, sonra gizle
            } else {
                toast.error(t("verification_code_send_failed"));
                try {
                    if (appVerifier && appVerifier.clear) {
                        appVerifier.clear();
                    }
                } catch (e) {
                    console.warn('reCAPTCHA temizleme hatası:', e);
                }
                if (window.recaptchaVerifier === appVerifier) {
                    try { delete window.recaptchaVerifier; } catch (_) { }
                }
                if (window.recaptchaContainer === recaptchaContainer) {
                    try { delete window.recaptchaContainer; } catch (_) { }
                }
                setTimeout(() => {
                    try {
                        if (recaptchaContainer) {
                            // Container'ı gizle ama silme
                            recaptchaContainer.style.display = 'none';
                            recaptchaContainer.style.visibility = 'hidden';
                            recaptchaContainer.style.position = 'absolute';
                            recaptchaContainer.style.left = '-9999px';
                            recaptchaContainer.style.top = '-9999px';
                            recaptchaContainer.innerHTML = '';
                        }
                    } catch (e) {
                        console.warn('Container gizleme hatası:', e);
                    }
                }, 1000);
            }
        } catch (err) {
            console.error("=== Firebase Telefon Doğrulama Hatası ===");
            console.error("Hata objesi:", err);
            console.error("Hata kodu:", err.code);
            console.error("Hata mesajı:", err.message);

            console.error('[UnifiedRegister] Firebase telefon doğrulama hatası:', {
                code: err?.code,
                message: err?.message,
                error: err
            });

            // Timeout hatası kontrolü - case insensitive ve farklı formatları kontrol et
            const isTimeoutError = err?.message?.toLowerCase().includes('timeout') ||
                err?.name === 'TimeoutError' ||
                err?.message === 'Timeout' ||
                err?.toString().toLowerCase().includes('timeout');

            if (isTimeoutError) {
                toast.error(t("recaptcha_verification_timeout"));
            } else if (err?.code === 'auth/too-many-requests') {
                const cleanPhoneNumber = values.phoneNumber?.replace(/\s/g, '') || '';
                const now = Date.now();
                setLastCodeSentTime(now);
                setCooldownSeconds(COOLDOWN_DURATION_AFTER_ERROR);
                const storageKey = getStorageKey(cleanPhoneNumber);
                localStorage.setItem(storageKey, JSON.stringify({
                    timestamp: now,
                    duration: COOLDOWN_DURATION_AFTER_ERROR
                }));
                toast.error(t("too_many_attempts_wait", { minutes: Math.floor(COOLDOWN_DURATION_AFTER_ERROR / 60) }) || `Çok fazla deneme yapıldı. Lütfen ${Math.floor(COOLDOWN_DURATION_AFTER_ERROR / 60)} dakika bekleyin.`);
            } else if (err?.code === 'auth/quota-exceeded') {
                const cleanPhoneNumber = values.phoneNumber?.replace(/\s/g, '') || '';
                const now = Date.now();
                setLastCodeSentTime(now);
                setCooldownSeconds(COOLDOWN_DURATION_AFTER_ERROR);
                const storageKey = getStorageKey(cleanPhoneNumber);
                localStorage.setItem(storageKey, JSON.stringify({
                    timestamp: now,
                    duration: COOLDOWN_DURATION_AFTER_ERROR
                }));
                toast.error(t("sms_quota_exceeded"));
            } else if (err?.code === 'auth/network-request-failed') {
                toast.error(t("network_error"));
            } else if (err?.code?.startsWith('auth/')) {
                toast.error(t("verification_code_send_failed"));
            } else {
                toast.error(t("verification_code_send_failed"));
            }

            try {
                if (window.recaptchaVerifier) {
                    try {
                        if (window.recaptchaVerifier.clear) {
                            window.recaptchaVerifier.clear();
                        }
                    } catch (e) {
                        console.warn('reCAPTCHA temizleme hatası:', e);
                    }
                    try { delete window.recaptchaVerifier; } catch (_) { }
                }
                if (window.recaptchaContainer) {
                    try { delete window.recaptchaContainer; } catch (_) { }
                }
                // Container'ı temizle - gizle ama silme
                const container = document.getElementById('recaptcha-container');
                if (container) {
                    setTimeout(() => {
                        try {
                            // Container'ı gizle ama silme
                            container.style.display = 'none';
                            container.style.visibility = 'hidden';
                            container.style.position = 'absolute';
                            container.style.left = '-9999px';
                            container.style.top = '-9999px';
                            container.innerHTML = '';
                        } catch (e) {
                            console.warn('Container gizleme hatası:', e);
                        }
                    }, 1000);
                }
            } catch (cleanupError) {
                console.warn('reCAPTCHA cleanup hatası:', cleanupError);
            }
        } finally {
            setLoading(false);
            isSendingCodeRef.current = false;
        }
    };

    const handleVerifyCode = async (code) => {
        if (!code || code.length !== 6) {
            return;
        }

        try {
            setLoading(true);
            console.log('[UnifiedRegister] Telefon doğrulama başladı...');

            // Firebase telefon doğrulama kodunu kontrol et
            if (window.confirmationResult) {
                console.log('[UnifiedRegister] Firebase doğrulama yapılıyor...');
                // Firebase confirm uzun sürebilir; UI 'Timeout' hatasını bastırmak için try/catch yapısı zaten mevcut
                const result = await window.confirmationResult.confirm(code);

                if (result.user) {
                    setVerifyCode(code);

                    try {
                        await handleOnSubmit(values);
                        setLoading(false);
                    } catch (submitError) {
                        console.error("Kayıt işlemi hatası:", submitError);
                        toast.error(t("registration_error"));
                        setLoading(false);
                        return;
                    }
                } else {
                    toast.error(t("invalid_verification_code"));
                    setLoading(false);
                }
            } else {
                toast.error(t("verification_session_not_found"));
                setLoading(false);
            }
        } catch (err) {
            console.error("Doğrulama kodu hatası:", err);
            const cleanPhoneNumber = values.phoneNumber?.replace(/\s/g, "") || "";
            const verifyIdentifier = cleanPhoneNumber ? `+90${cleanPhoneNumber}` : null;
            reportClientAuthError({
                provider: "FirebasePhone",
                stage: "verifyCode",
                identifier: verifyIdentifier,
                error: err
            });

            if (err?.message === 'Timeout' || err?.code === 'auth/timeout') {
                setLoading(false);
                return;
            }

            const isFirebaseError = err?.code?.startsWith('auth/') ||
                err?.message?.includes('Firebase') ||
                err?.message?.includes('too-many-requests') ||
                err?.message?.includes('quota-exceeded') ||
                err?.message?.includes('network-request-failed');

            if (err.code === 'auth/invalid-verification-code') {
                toast.error(t("invalid_verification_code"));
            } else if (err.code === 'auth/code-expired') {
                toast.error(t("verification_code_expired"));
            } else if (isFirebaseError) {
                console.error('[UnifiedRegister] Firebase doğrulama kodu hatası (kullanıcıya gösterilmedi):', {
                    code: err?.code,
                    message: err?.message,
                    error: err
                });
            } else {
                toast.error(t("verification_error"));
            }
            setLoading(false);
        }
    };

    // Yeni kod gönderme fonksiyonu (Verify component için)
    const handleResendCode = async () => {
        // Prevent multiple simultaneous calls
        if (isSendingCodeRef.current) {
            return;
        }

        isSendingCodeRef.current = true;
        try {
            const cleanPhoneNumber = values.phoneNumber.replace(/\s/g, '');
            const fullPhoneNumber = `+90${cleanPhoneNumber}`;

            const recaptchaContainer = document.createElement('div');
            recaptchaContainer.id = 'recaptcha-container-resend';
            recaptchaContainer.style.position = 'absolute';
            recaptchaContainer.style.left = '-9999px';
            recaptchaContainer.style.top = '-9999px';
            recaptchaContainer.style.visibility = 'hidden';
            document.body.appendChild(recaptchaContainer);

            // reCAPTCHA'yı oluştur
            const appVerifier = await generateRecaptcha(recaptchaContainer);

            if (!appVerifier) {
                toast.error(t("recaptcha_load_failed"));
                setTimeout(() => {
                    try {
                        if (recaptchaContainer) {
                            // Container'ı gizle ama silme
                            recaptchaContainer.style.display = 'none';
                            recaptchaContainer.style.visibility = 'hidden';
                            recaptchaContainer.style.position = 'absolute';
                            recaptchaContainer.style.left = '-9999px';
                            recaptchaContainer.style.top = '-9999px';
                            recaptchaContainer.innerHTML = '';
                        }
                    } catch (e) {
                        console.warn('Container gizleme hatası:', e);
                    }
                }, 1000);
                return;
            }

            // Firebase telefon doğrulaması
            let confirmationResult;
            try {
                confirmationResult = await signInWithPhoneNumberFirebase(
                    fullPhoneNumber,
                    appVerifier,
                    () => { } // setDialog callback'i
                );
            } catch (signInError) {
                // Timeout hatası kontrolü
                const isTimeoutError = signInError?.message?.toLowerCase().includes('timeout') ||
                    signInError?.name === 'TimeoutError' ||
                    signInError?.message === 'Timeout' ||
                    signInError?.toString().toLowerCase().includes('timeout') ||
                    signInError?.code === 'auth/timeout';
                
                if (isTimeoutError) {
                    toast.error(t("recaptcha_verification_timeout"));
                }
                
                // Cleanup
                try {
                    if (appVerifier && appVerifier.clear) {
                        appVerifier.clear();
                    }
                } catch (e) {
                    console.warn('reCAPTCHA temizleme hatası:', e);
                }
                setTimeout(() => {
                    try {
                        if (recaptchaContainer) {
                            // Container'ı gizle ama silme - reCAPTCHA widget'ı hala erişmeye çalışabilir
                            recaptchaContainer.style.display = 'none';
                            recaptchaContainer.style.visibility = 'hidden';
                            recaptchaContainer.style.position = 'absolute';
                            recaptchaContainer.style.left = '-9999px';
                            recaptchaContainer.style.top = '-9999px';
                            // İçeriği temizle
                            recaptchaContainer.innerHTML = '';
                        }
                    } catch (e) {
                        console.warn('Container gizleme hatası:', e);
                    }
                }, 1000); // 1 saniye bekle, sonra gizle
                // Re-throw to be caught by outer catch block
                throw signInError;
            }

            if (confirmationResult) {
                window.confirmationResult = confirmationResult;
                toast.success(t("new_verification_code_sent"));

                try {
                    if (appVerifier && appVerifier.clear) {
                        appVerifier.clear();
                    }
                } catch (e) {
                    console.warn('reCAPTCHA temizleme hatası:', e);
                }
                if (window.recaptchaVerifier === appVerifier) {
                    try { delete window.recaptchaVerifier; } catch (_) { }
                }
                if (window.recaptchaContainer === recaptchaContainer) {
                    try { delete window.recaptchaContainer; } catch (_) { }
                }
                setTimeout(() => {
                    try {
                        if (recaptchaContainer) {
                            // Container'ı gizle ama silme
                            recaptchaContainer.style.display = 'none';
                            recaptchaContainer.style.visibility = 'hidden';
                            recaptchaContainer.style.position = 'absolute';
                            recaptchaContainer.style.left = '-9999px';
                            recaptchaContainer.style.top = '-9999px';
                            recaptchaContainer.innerHTML = '';
                        }
                    } catch (e) {
                        console.warn('Container gizleme hatası:', e);
                    }
                }, 1000);
            } else {
                toast.error(t("new_verification_code_failed"));
                try {
                    if (appVerifier && appVerifier.clear) {
                        appVerifier.clear();
                    }
                } catch (e) {
                    console.warn('reCAPTCHA temizleme hatası:', e);
                }
                if (window.recaptchaVerifier === appVerifier) {
                    try { delete window.recaptchaVerifier; } catch (_) { }
                }
                if (window.recaptchaContainer === recaptchaContainer) {
                    try { delete window.recaptchaContainer; } catch (_) { }
                }
                setTimeout(() => {
                    try {
                        if (recaptchaContainer) {
                            // Container'ı gizle ama silme
                            recaptchaContainer.style.display = 'none';
                            recaptchaContainer.style.visibility = 'hidden';
                            recaptchaContainer.style.position = 'absolute';
                            recaptchaContainer.style.left = '-9999px';
                            recaptchaContainer.style.top = '-9999px';
                            recaptchaContainer.innerHTML = '';
                        }
                    } catch (e) {
                        console.warn('Container gizleme hatası:', e);
                    }
                }, 1000);
            }
        } catch (err) {
            console.error("Yeni kod gönderme hatası:", err);
            
            // Timeout hatası kontrolü
            const isTimeoutError = err?.message?.toLowerCase().includes('timeout') ||
                err?.name === 'TimeoutError' ||
                err?.message === 'Timeout' ||
                err?.toString().toLowerCase().includes('timeout') ||
                err?.code === 'auth/timeout';
            
            if (isTimeoutError) {
                toast.error(t("recaptcha_verification_timeout"));
            } else {
                toast.error(t("new_verification_code_failed"));
            }

            try {
                if (window.recaptchaVerifier) {
                    try {
                        if (window.recaptchaVerifier.clear) {
                            window.recaptchaVerifier.clear();
                        }
                    } catch (e) {
                        console.warn('reCAPTCHA temizleme hatası:', e);
                    }
                    try { delete window.recaptchaVerifier; } catch (_) { }
                }
                if (window.recaptchaContainer) {
                    try { delete window.recaptchaContainer; } catch (_) { }
                }
                // Container'ı temizle - gizle ama silme
                const container = document.getElementById('recaptcha-container-resend');
                if (container) {
                    setTimeout(() => {
                        try {
                            // Container'ı gizle ama silme
                            container.style.display = 'none';
                            container.style.visibility = 'hidden';
                            container.style.position = 'absolute';
                            container.style.left = '-9999px';
                            container.style.top = '-9999px';
                            container.innerHTML = '';
                        } catch (e) {
                            console.warn('Container gizleme hatası:', e);
                        }
                    }, 1000);
                }
            } catch (cleanupError) {
                console.warn('reCAPTCHA cleanup hatası:', cleanupError);
            }
        } finally {
            isSendingCodeRef.current = false;
        }
    };

    const inputFieldStyle = {
        "& .MuiOutlinedInput-root": {
            borderRadius: "14px",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            background: appColors.bgMuted,
            "&:hover": {
                background: appColors.bgMuted,
                "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: appColors.primary,
                    borderWidth: "2px",
                }
            },
            "&.Mui-focused": {
                background: appColors.white,
                "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: appColors.primary,
                    borderWidth: "2px",

                }
            },
            "&.Mui-disabled": {
                background: appColors.bgMuted,
            },
        },
        "& .MuiInputLabel-root": {
            color: appColors.gray500,
            fontWeight: 500,
            "&.Mui-focused": {
                color: appColors.primary,
                fontWeight: 600,
            },
            "&.Mui-disabled": {
                color: `${appColors.gray500} !important`,
            },
        },
        "& .MuiInputBase-input": {
            fontWeight: 500,
            "&.Mui-disabled": {
                WebkitTextFillColor: appColors.gray900,
                color: appColors.gray900,
                opacity: 1,
            },
        },
    };

    return (
        <>
            <form onSubmit={handleSubmit}>
                    <Fade in timeout={400}>
                        <Box
                            aria-hidden={showVerify}
                            sx={{
                                pointerEvents: showVerify ? "none" : "auto",
                                userSelect: showVerify ? "none" : "auto",
                            }}
                        >
                            {/* Section Title */}
                            <Typography
                                variant="h5"
                                sx={{
                                    color: appColors.gray900,
                                    mb: 3,
                                    fontWeight: 600,
                                    textAlign: "left",
                                }}
                            >
                                {t("personal_information")}
                            </Typography>

                            {userType === 'doctor' && (
                                <Box sx={{ mb: 3, width: '100%' }}>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            color: appColors.gray900,
                                            mb: 1.5,
                                            textAlign: "left",
                                            fontSize: "0.95rem",
                                            fontWeight: 500
                                        }}
                                    >
                                        {t("specialty_area")}
                                    </Typography>
                                    <FormControl fullWidth>
                                        <Select
                                            name="professionId"
                                            value={values.professionId || ''}
                                            onChange={handleChange}
                                            displayEmpty
                                            sx={{
                                                borderRadius: "8px",
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: appColors.gray200,
                                                },
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: appColors.primary,
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: appColors.primary,
                                                },
                                            }}
                                        >
                                            <MenuItem value="" disabled>
                                                <em>{t("select_specialty_area")}</em>
                                            </MenuItem>
                                            {categoriesLoading ? (
                                                <MenuItem disabled>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <CircularProgress size={16} />
                                                        <Typography variant="body2">{t("categories_loading")}</Typography>
                                                    </Box>
                                                </MenuItem>
                                            ) : categoriesError ? (
                                                <MenuItem disabled>
                                                    <Typography variant="body2" color="error">
                                                        Kategoriler yüklenirken hata oluştu
                                                    </Typography>
                                                </MenuItem>
                                            ) : specialtyCategories.length === 0 ? (
                                                <MenuItem disabled>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Aktif kategori bulunamadı
                                                    </Typography>
                                                </MenuItem>
                                            ) : (
                                                specialtyCategories.map((category) => (
                                                    <MenuItem key={category.id} value={category.id}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <School sx={{ fontSize: 20, color: appColors.primary }} />
                                                            <Typography variant="body2">
                                                                {getTaxonomyEntityName(category)}
                                                            </Typography>
                                                        </Box>
                                                    </MenuItem>
                                                ))
                                            )}
                                        </Select>
                                    </FormControl>
                                    {touched.professionId && errors.professionId && (
                                        <Typography
                                            variant="caption"
                                            color="error"
                                            sx={{
                                                display: "block",
                                                textAlign: "left",
                                                mt: 1,
                                                fontSize: "0.8rem",
                                                fontWeight: 500
                                            }}
                                        >
                                            {errors.professionId}
                                        </Typography>
                                    )}
                                </Box>
                            )}

                            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    value={values.firstName}
                                    id="firstName"
                                    name="firstName"
                                    inputProps={{
                                        maxLength: 50
                                    }}
                                    label={t("first_name_label")}
                                    placeholder={t("first_name_placeholder")}
                                    onChange={handleChange}
                                    helperText={touched.firstName && errors.firstName}
                                    error={touched.firstName && Boolean(errors.firstName)}
                                    onBlur={handleBlur}
                                    sx={inputFieldStyle}
                                    InputProps={{
                                        startAdornment: (
                                            <Person sx={{ color: appColors.gray500, mr: 1, fontSize: 20 }} />
                                        ),
                                    }}
                                />
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    value={values.lastName}
                                    id="lastName"
                                    name="lastName"
                                    inputProps={{
                                        maxLength: 50
                                    }}
                                    label={t("last_name_label")}
                                    placeholder={t("last_name_placeholder")}
                                    onChange={handleChange}
                                    helperText={touched.lastName && errors.lastName}
                                    error={touched.lastName && Boolean(errors.lastName)}
                                    onBlur={handleBlur}
                                    sx={inputFieldStyle}
                                    InputProps={{
                                        startAdornment: (
                                            <Person sx={{ color: appColors.gray500, mr: 1, fontSize: 20 }} />
                                        ),
                                    }}
                                />
                            </Box>

                            <TextField
                                fullWidth
                                variant="outlined"
                                value={values.email}
                                id="email"
                                name="email"
                                autoComplete="new-email"
                                inputProps={{
                                    maxLength: 320
                                }}
                                label={t("email_label")}
                                placeholder={t("email_placeholder")}
                                onChange={handleChange}
                                helperText={touched.email && errors.email}
                                error={touched.email && Boolean(errors.email)}
                                onBlur={handleBlur}
                                sx={{ ...inputFieldStyle, mb: 2.5 }}
                                InputProps={{
                                    startAdornment: (
                                        <Email sx={{ color: appColors.gray500, mr: 1, fontSize: 20 }} />
                                    ),
                                }}
                            />

                            <Box sx={{ display: 'flex', gap: 2, mb: 2.5 }}>
                                <TextField
                                    variant="outlined"
                                    name="countryCode"
                                    label={t("country_code_label")}
                                    value={values.countryCode}
                                    disabled={true}
                                    sx={{ ...inputFieldStyle, minWidth: "120px" }}
                                    InputProps={{
                                        startAdornment: (
                                            <Phone sx={{ color: appColors.gray500, mr: 1, fontSize: 20 }} />
                                        ),
                                    }}
                                />
                                <InputMask
                                    mask="999 999 99 99"
                                    value={values.phoneNumber}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                >
                                    {(inputProps) => (
                                        <TextField
                                            {...inputProps}
                                            fullWidth
                                            variant="outlined"
                                            name="phoneNumber"
                                            label={t("phone_number_label")}
                                            placeholder={t("phone_placeholder")}
                                            helperText={touched.phoneNumber && errors.phoneNumber}
                                            error={touched.phoneNumber && Boolean(errors.phoneNumber)}
                                            sx={inputFieldStyle}
                                            InputProps={{
                                                startAdornment: (
                                                    <Phone sx={{ color: appColors.gray500, mr: 1, fontSize: 20 }} />
                                                ),
                                            }}
                                        />
                                    )}
                                </InputMask>
                            </Box>

                            {!isDoctorRegister && (
                            <Box sx={{ display: 'flex', gap: 2, mb: 2.5 }}>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    value={values.password}
                                    id="password"
                                    name="password"
                                    autoComplete="new-password"
                                    inputProps={{
                                        maxLength: 32
                                    }}
                                    label={t("password_label")}
                                    placeholder={t("password_placeholder")}
                                    onChange={handleChange}
                                    type={passwordVisibility ? "text" : "password"}
                                    helperText={touched.password && errors.password}
                                    error={touched.password && Boolean(errors.password)}
                                    onBlur={handleBlur}
                                    sx={inputFieldStyle}
                                    InputProps={{
                                        startAdornment: (
                                            <Lock sx={{ color: appColors.gray500, mr: 1, fontSize: 20 }} />
                                        ),
                                        endAdornment: (
                                            <IconButton
                                                onClick={() => setPasswordVisibility(x => !x)}
                                                sx={{
                                                    color: appColors.gray500,
                                                    "&:hover": {
                                                        color: appColors.info,
                                                        transform: "scale(1.1)",
                                                    },
                                                    transition: "all 0.2s ease"
                                                }}
                                            >
                                                {passwordVisibility ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        ),
                                    }}
                                />
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    value={values.confirmPassword}
                                    id="confirmPassword"
                                    name="confirmPassword"
                                    inputProps={{
                                        maxLength: 32
                                    }}
                                    label={t("confirm_password_label")}
                                    placeholder={t("confirm_password_placeholder")}
                                    onChange={handleChange}
                                    type={passwordConfirmVisibility ? "text" : "password"}
                                    helperText={touched.confirmPassword && errors.confirmPassword}
                                    error={touched.confirmPassword && Boolean(errors.confirmPassword)}
                                    onBlur={handleBlur}
                                    sx={inputFieldStyle}
                                    InputProps={{
                                        startAdornment: (
                                            <Lock sx={{ color: appColors.gray500, mr: 1, fontSize: 20 }} />
                                        ),
                                        endAdornment: (
                                            <IconButton
                                                onClick={() => setPasswordConfirmVisibility(x => !x)}
                                                sx={{
                                                    color: appColors.gray500,
                                                    "&:hover": {
                                                        color: appColors.info,
                                                        transform: "scale(1.1)",
                                                    },
                                                    transition: "all 0.2s ease"
                                                }}
                                            >
                                                {passwordConfirmVisibility ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        ),
                                    }}
                                />
                            </Box>
                            )}

                            {/* <Alert
                                severity="info"
                                sx={{
                                    borderRadius: "8px",
                                    mb: 2,
                                    background: `linear-gradient(135deg, ${appColors.primaryBgHover} 0%, ${appColors.tealMint} 100%)`,
                                    border: `1px solid ${appColors.primary}`,
                                    color: appColors.primaryDark,
                                    "& .MuiAlert-icon": {
                                        color: appColors.primary,
                                        fontSize: "1.2rem"
                                    },
                                    "& .MuiAlert-message": {
                                        fontWeight: 500,
                                        fontSize: "0.95rem"
                                    }
                                }}
                            >
                                Devam etmek için kullanıcı sözleşmelerini onaylamanız gerekmektedir.
                            </Alert>

                            <AgreementsList handleIsAllSelected={setIsAllAgreementsSelected} onSelectionChange={setSelectedAgreementKeys} /> */}

                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 2 }}>
                                <Info sx={{ fontSize: "1rem", color: appColors.primary }} />
                                <Typography variant="body2" sx={{ color: appColors.gray500 }}>
                                    Aydınlatma metnine <Link
                                        component="button"
                                        type="button"
                                        variant="body2"
                                        onClick={() => setShowKvkkDialog(true)}
                                        sx={{
                                            color: appColors.primary,
                                            textDecoration: "underline",
                                            fontWeight: 600,
                                            cursor: "pointer",
                                            verticalAlign: "baseline",
                                            textTransform: "none",
                                            "&:hover": {
                                                color: appColors.primaryDark,
                                            }
                                        }}
                                    >buradan</Link> ulaşabilirsiniz.
                                </Typography>
                            </Box>

                            <Button
                                sx={{
                                    marginTop: "1.5rem",
                                    height: "48px",
                                    borderRadius: "8px",
                                    background: `linear-gradient(135deg, ${appColors.primary} 0%, ${appColors.primaryTeal} 100%)`,
                                    textTransform: "none",
                                    fontWeight: 600,
                                    fontSize: "1rem",

                                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                                    "&:hover": {
                                        transform: "translateY(-2px)",

                                        background: `linear-gradient(135deg, ${appColors.primaryDark} 0%, ${appColors.primary} 100%)`,
                                    },
                                    "&:disabled": {
                                        background: appColors.gray200,
                                        transform: "none",

                                        color: appColors.gray500
                                    }
                                }}
                                name="continue"
                                type="button"
                                variant="contained"
                                fullWidth
                                onClick={async () => {
                                    handleSendCode();
                                }}
                                disabled={!isValid || !dirty || loading}
                            >
                                {loading ? t("loading") : t("continue")}
                            </Button>

                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 2,
                                    mt: 3,
                                }}
                            >
                                <Box sx={{ textAlign: "center", mt: 2 }}>
                                    <Typography variant="body2" sx={{ color: appColors.gray500, mb: 1 }}>
                                        Zaten hesabınız var mı?{" "}
                                        <Link
                                            component="button"
                                            variant="body2"
                                            onClick={() => navigate('/login')}
                                            sx={{
                                                color: appColors.primary,
                                                textDecoration: "underline",
                                                fontWeight: 600,
                                                "&:hover": {
                                                    color: appColors.primaryDark,
                                                }
                                            }}
                                        >
                                            Giriş Yap
                                        </Link>
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Fade>
            </form>

            {showVerify && (
                <RegisterPhoneVerification
                    phoneNumber={`+90${values.phoneNumber.replace(/\s/g, '')}`}
                    onBack={handleCloseVerify}
                    onVerify={handleVerifyCode}
                    onResendCode={handleResendCode}
                    loading={loading}
                    title={t("verify_phone_title")}
                    subtitle={t("verify_phone_subtitle")}
                    verifyButtonText={loading ? t("loading") : t("verify")}
                />
            )}

            <Dialog
                open={showKvkkDialog}
                onClose={() => setShowKvkkDialog(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: "12px",
                        maxHeight: "80vh"
                    }
                }}
            >
                <DialogTitle sx={{
                    fontWeight: 700,
                    color: appColors.primary,
                    borderBottom: `1px solid ${appColors.gray200}`,
                    pb: 2
                }}>
                    KİŞİSEL VERİLERİN İŞLENMESİNE İLİŞKİN AYDINLATMA METNİ
                </DialogTitle>
                <DialogContent sx={{ mt: 2 }}>
                    <KvkkAydinlatmaMetniContent />
                </DialogContent>
                <DialogActions sx={{ p: 2, borderTop: `1px solid ${appColors.gray200}` }}>
                    <Button
                        onClick={() => setShowKvkkDialog(false)}
                        variant="contained"
                        sx={{
                            background: `linear-gradient(135deg, ${appColors.primary} 0%, ${appColors.primaryTeal} 100%)`,
                            textTransform: "none",
                            fontWeight: 600,
                            borderRadius: "8px",
                            px: 4,
                            "&:hover": {
                                background: `linear-gradient(135deg, ${appColors.primaryDark} 0%, ${appColors.primary} 100%)`,
                            }
                        }}
                    >
                        Kapat
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default UnifiedRegister;
